import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
    Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Typography, FormControlLabel, Checkbox,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { BuyerService } from '../../services/buyer.service';
import { BuyerOrderStatus, IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { PERMISSIONS } from '../../constants/permissions';
import { ALL_BUYER_ORDER_STATUSES } from './buyerOrderTransitions';
import BuyerAutocomplete from './BuyerAutocomplete';
import CommercialPartyAutocomplete from '../commercial/CommercialPartyAutocomplete';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import OrderProgressCell from '../dashboard/thread/OrderProgressCell';
import { useThreadOrders } from '../dashboard/thread/useThreadData';

/** Days to ship means nothing once the goods have shipped or the order is closed/cancelled. */
const TERMINAL_STATUSES: BuyerOrderStatus[] = ['SHIPPED', 'CLOSED', 'CANCELLED'];

const STATUS_COLORS: Record<BuyerOrderStatus, 'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'> = {
    DRAFT: 'default',
    CONFIRMED: 'info',
    IN_PRODUCTION: 'primary',
    SHIPPED: 'success',
    CLOSED: 'default',
    CANCELLED: 'error',
};

/** Buyer order list (garments-merchandising T10) — G20: MUI Table + TablePagination, never
 *  DataGrid, server-side paged through the Page<T> endpoint. Filters by buyer/status/ship-date
 *  range; each row surfaces the ship month and days-to-ship so a merchandiser can eyeball what is
 *  due soon without opening the order. Row click navigates to `BuyerOrderForm`. */
export function BuyerOrderList() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const toast = useToast();
    const canManage = hasPermission(PERMISSIONS.BUYER_ORDER_MANAGE);
    const canSeeProgress = hasPermission(PERMISSIONS.MERCH_REPORT_VIEW);

    const [rows, setRows] = useState<IBuyerOrder[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [buyerFilter, setBuyerFilter] = useState<IBuyer | null>(null);
    const [statusFilter, setStatusFilter] = useState<BuyerOrderStatus | 'ALL'>('ALL');
    const [shipFrom, setShipFrom] = useState('');
    const [shipTo, setShipTo] = useState('');
    const [agentFilterId, setAgentFilterId] = useState<string | null>(null);
    const [showCancelled, setShowCancelled] = useState(false);
    const [loading, setLoading] = useState(true);

    const institutionId = AuthService.getSelectedInstitution() ?? '';
    // Progress column: ONE request for the whole list (never one per row), only for people who may read it.
    const threadOrders = useThreadOrders(institutionId, canSeeProgress);
    const progressByOrder = useMemo(
        () => new Map((threadOrders.rows ?? []).map(row => [row.orderId, row])),
        [threadOrders.rows],
    );

    // Id -> label lookup for the Buyer column — the order DTO carries only `buyerId`.
    const [buyerLabels, setBuyerLabels] = useState<Record<string, string>>({});

    const loadBuyerLabels = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            const res = await BuyerService.list(institutionId, 0, 200);
            setBuyerLabels(Object.fromEntries(res.content.map(b => [b.id, `${b.name} (${b.code})`])));
        } catch (e) {
            // Non-fatal — the table falls back to a short id when a lookup is missing.
        }
    }, []);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await BuyerOrderService.list(
                institutionId, page, size,
                buyerFilter?.id,
                statusFilter === 'ALL' ? undefined : statusFilter,
                shipFrom || undefined,
                shipTo || undefined,
                agentFilterId || undefined,
                showCancelled
            );
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load orders');
        } finally {
            setLoading(false);
        }
    }, [page, size, buyerFilter, statusFilter, shipFrom, shipTo, agentFilterId, showCancelled, toast]);

    useEffect(() => {
        loadBuyerLabels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size, buyerFilter, statusFilter, shipFrom, shipTo, agentFilterId, showCancelled]);

    // Hiding cancelled orders and the newest-order-date-first sort are applied by the server.
    const visibleRows = rows;

    const buyerLabel = (id: string) => buyerLabels[id] ?? `${id.slice(0, 8)}…`;
    const shipMonth = (shipDate: string) => format(parseISO(shipDate), 'MMM yyyy');
    const daysToShip = (shipDate: string) => differenceInCalendarDays(parseISO(shipDate), new Date());

    const clearFilters = () => {
        setBuyerFilter(null);
        setStatusFilter('ALL');
        setShipFrom('');
        setShipTo('');
        setAgentFilterId(null);
        setShowCancelled(false);
        setPage(0);
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.orders.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5">Buyer Orders</Typography>
                {canManage && (
                    <Button data-help="merchandising.orders.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/merchandising/orders/new')}>
                        New Order
                    </Button>
                )}
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.orders.filter">
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                    <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                        <BuyerAutocomplete
                            data-help="merchandising.orders.filterBuyer"
                            value={buyerFilter}
                            onChange={b => { setBuyerFilter(b); setPage(0); }}
                            label="Buyer" size="small"
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 260px', maxWidth: 320 }}>
                        <CommercialPartyAutocomplete
                            data-help="merchandising.orders.filterAgent"
                            institutionId={institutionId}
                            partyTypes={['BUYING_AGENT']}
                            label="Agent" size="small"
                            value={agentFilterId}
                            onChange={party => { setAgentFilterId(party?.id ?? null); setPage(0); }}
                        />
                    </Box>
                    <FormControl data-help="merchandising.orders.filterStatus" size="small" sx={{ flex: '1 1 170px', maxWidth: 240 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status" value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value as BuyerOrderStatus | 'ALL'); setPage(0); }}
                        >
                            <MenuItem value="ALL">All</MenuItem>
                            {ALL_BUYER_ORDER_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        data-help="merchandising.orders.shipFrom" label="Ship From" type="date" size="small" sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        InputLabelProps={{ shrink: true }}
                        value={shipFrom} onChange={e => { setShipFrom(e.target.value); setPage(0); }}
                    />
                    <TextField
                        data-help="merchandising.orders.shipTo" label="Ship To" type="date" size="small" sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        InputLabelProps={{ shrink: true }}
                        value={shipTo} onChange={e => { setShipTo(e.target.value); setPage(0); }}
                    />
                    <FormControlLabel
                        data-help="merchandising.orders.showCancelled"
                        control={<Checkbox size="small" checked={showCancelled} onChange={e => { setShowCancelled(e.target.checked); setPage(0); }} />}
                        label="Show cancelled"
                    />
                    <Button data-help="merchandising.orders.clear" size="small" onClick={clearFilters}>Clear</Button>
                </Stack>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} data-help-section="merchandising.orders.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.orders.col.number">Order Number</TableCell>
                                <TableCell data-help="merchandising.orders.col.buyer">Buyer</TableCell>
                                <TableCell data-help="merchandising.orders.col.agent">Agent</TableCell>
                                <TableCell data-help="merchandising.orders.col.shipDate">Ship Date</TableCell>
                                <TableCell data-help="merchandising.orders.col.shipMonth">Ship Month</TableCell>
                                <TableCell align="right" data-help="merchandising.orders.daysToShip">Days to Ship</TableCell>
                                <TableCell data-help="merchandising.orders.col.status">Status</TableCell>
                                <TableCell data-help="merchandising.orders.col.progress">Progress</TableCell>
                                <TableCell align="right" data-help="merchandising.orders.col.qty">Total Qty</TableCell>
                                <TableCell align="right" data-help="merchandising.orders.col.value">Total Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map(r => (
                                <TableRow
                                    key={r.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/merchandising/orders/${r.id}`)}
                                    data-testid={`order-row-${r.id}`}
                                >
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {r.orderNumber}
                                    </TableCell>
                                    <TableCell>{buyerLabel(r.buyerId)}</TableCell>
                                    <TableCell>
                                        {r.effectiveAgentPartyName ?? '—'}
                                        {r.agentSource === 'BUYER' && (
                                            <Chip data-help="merchandising.orders.agentDefault" size="small" label="Buyer default" sx={{ ml: 0.5 }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.shipDate)}</TableCell>
                                    <TableCell>{shipMonth(r.shipDate)}</TableCell>
                                    <TableCell align="right">{TERMINAL_STATUSES.includes(r.status) ? '—' : daysToShip(r.shipDate)}</TableCell>
                                    <TableCell>
                                        <Chip data-help="merchandising.orders.status" size="small" color={STATUS_COLORS[r.status]} label={enumLabel(r.status)} />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        <OrderProgressCell orderId={r.id} orderNumber={r.orderNumber} row={progressByOrder.get(r.id)} />
                                    </TableCell>
                                    <TableCell align="right">{r.totalQuantity ?? 0}</TableCell>
                                    <TableCell align="right">{formatMoney(r.totalValue ?? 0, r.currencyCode)}</TableCell>
                                </TableRow>
                            ))}
                            {visibleRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            No orders match this filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}
        </Box>
    );
}

export default BuyerOrderList;
